import {
  useEffect,
  useId,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
} from 'react'
import SegmentedControl from '@features/shared/components/SegmentedControl'
import Popover from '@features/shared/components/Popover'
import { useUpdateOwnPreference } from '@features/settings/hooks/useUpdateOwnPreference'
import { PREFERENCE_SEGMENT_ICONS } from '@features/settings/logic/preferenceSegmentIcons'
import {
  SETTING_DEFINITIONS,
  isSidebarNavigationMode,
  useSettingsStore,
  type SidebarNavigationMode,
} from '@features/settings/stores/useSettingsStore'
import { HOVER_MENU_CLOSE_DELAY_MS, RAIL_HOVER_INTENT_MS } from '@/styling/tokens.interaction'

const MODE_ICONS = PREFERENCE_SEGMENT_ICONS.sidebar_navigation_mode ?? {}

/** The Expansion choices, each with the icon it has on the Preferences page. */
const MODE_OPTIONS = (SETTING_DEFINITIONS.sidebarNavigationMode.options ?? []).flatMap((option) =>
  isSidebarNavigationMode(option.value)
    ? [{ value: option.value, label: option.label, icon: MODE_ICONS[option.value] ?? null }]
    : []
)

/** The current mode's icon and, in the expanded rail, a name for its popup. */
export default function SidebarModeControl({
  isExpanded = false,
  onOpenChange,
}: {
  isExpanded?: boolean
  onOpenChange?: (open: boolean) => void
}) {
  const mode = useSettingsStore((s) => s.sidebarNavigationMode)
  const current = MODE_OPTIONS.find((option) => option.value === mode)
  const updateOwnPreference = useUpdateOwnPreference()
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLSpanElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Escape hands focus back to the button; that focus shouldn't reopen it.
  const refocusingRef = useRef(false)
  const cardId = useId()
  const changeOpen = (next: boolean) => {
    setOpen(next)
    onOpenChange?.(next)
  }

  const clearTimer = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }
  useEffect(() => clearTimer, [])

  const handleMouseEnter = () => {
    clearTimer()
    if (isExpanded) {
      changeOpen(true)
      return
    }
    if (!open) timerRef.current = setTimeout(() => changeOpen(true), RAIL_HOVER_INTENT_MS)
  }
  const handleMouseLeave = (event: MouseEvent<HTMLDivElement>) => {
    clearTimer()
    if (!open) return
    // Another rail item takes over at once; a slip into the page gets grace.
    const to = event.relatedTarget
    if (to instanceof Element && to.closest('[data-app-sidebar]')) {
      changeOpen(false)
      return
    }
    timerRef.current = setTimeout(() => changeOpen(false), HOVER_MENU_CLOSE_DELAY_MS)
  }
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape' || !open) return
    event.stopPropagation()
    changeOpen(false)
    // Focus moves at once, inside this call, so the flag covers only it.
    refocusingRef.current = true
    buttonRef.current?.focus()
    refocusingRef.current = false
  }
  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    const target = event.relatedTarget as Node | null
    if (!event.currentTarget.contains(target) && !cardRef.current?.contains(target)) {
      changeOpen(false)
    }
  }
  const choose = (value: SidebarNavigationMode) => {
    if (value === mode) return
    clearTimer()
    changeOpen(true)
    void updateOwnPreference('sidebar_navigation_mode', 'sidebarNavigationMode', value)
  }

  const choices = (
    <div ref={cardRef} className="w-max overflow-hidden rounded-md">
      <SegmentedControl
        withIcons
        size="tile"
        options={MODE_OPTIONS}
        value={mode}
        onChange={choose}
        ariaLabel="Sidebar Mode"
        className="!border-[color:var(--page-card-border-color)]"
      />
    </div>
  )

  return (
    <div
      data-sidebar-expansion-control
      className="relative flex h-[calc(var(--sidebar-tile-size)+var(--page-card-gap))] w-full shrink-0 items-start px-[var(--sidebar-item-inset)] pb-[var(--page-card-gap)]"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      onFocus={(event) => {
        if (!refocusingRef.current && event.target.matches(':focus-visible')) {
          clearTimer()
          changeOpen(true)
        }
      }}
    >
      {/* The rail animates its width. Anchor at its destination so the choices
          never slide between hover targets while the pointer is on them. */}
      <span
        ref={anchorRef}
        aria-hidden="true"
        className="pointer-events-none absolute top-0 h-[var(--sidebar-tile-size)] w-0"
        style={{
          left: `calc(var(${isExpanded ? '--sidebar-width-expanded' : '--sidebar-width-collapsed'}) - var(--sidebar-item-inset))`,
        }}
      />
      <Popover
        open={open}
        onOpenChange={changeOpen}
        triggerMode="manual"
        placement="right"
        anchorElementRef={anchorRef}
        sideOffsetPx={4}
        viewportPaddingPx={0}
        showCaret={false}
        wrapperClassName="w-full min-w-0"
        contentId={cardId}
        contentClassName="p-0"
        contentStyle={{
          border: 0,
          borderRadius: 0,
          background: 'transparent',
          boxShadow: 'none',
        }}
        trigger={
          <button
            ref={buttonRef}
            type="button"
            aria-label={current ? `Sidebar Mode: ${current.label}` : 'Sidebar Mode'}
            aria-expanded={open}
            aria-haspopup="dialog"
            aria-controls={cardId}
            onClick={() => {
              // Opens, never closes: a click just after it opened on hover keeps it open.
              clearTimer()
              changeOpen(true)
            }}
            // One tile like the rail's: the same hover and open fills.
            className={`flex h-[var(--sidebar-tile-size)] w-full min-w-0 items-center rounded-md pr-2 transition-[background-color,color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--accent-info)] ${
              open
                ? 'bg-[color:var(--sidebar-item-open-bg)] text-default'
                : 'text-muted hover:bg-[color:var(--sidebar-item-hover-bg)]'
            }`}
          >
            <span
              aria-hidden="true"
              className="flex h-full w-[calc(var(--sidebar-width-collapsed)-var(--sidebar-item-inset)*2)] shrink-0 items-center justify-center [&_svg]:h-sidebar-module-size [&_svg]:w-sidebar-module-size"
            >
              {current?.icon}
            </span>
            {isExpanded && (
              <>
                <span className="whitespace-nowrap text-sm font-medium">Sidebar Mode</span>
                <svg
                  aria-hidden="true"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  className="ml-auto shrink-0"
                >
                  <path
                    d="M5 3.5L8.5 7L5 10.5"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </>
            )}
          </button>
        }
      >
        {choices}
      </Popover>
    </div>
  )
}
